import { useEffect, useState } from "react";
import { ApiRequestError } from "../../../api/util";
import { ActiveDelegationModel } from "../../../interfaces/delegation";
import { useUpdateDelegationPermissions } from "../../../hooks/delegation/useUpdateDelegationPermissions";
import { useUpdateDelegationValidity } from "../../../hooks/delegation/useUpdateDelegationValidity";
import { formatDanishLongDate } from "../../../util/date";
import { Button } from "../../Button";
import Spinner from "../../graphics/Spinner";
import Modal, { ModalRoot } from "../../Modal/Modal";
import DelegationPermissionsField from "./DelegationPermissionsField";

interface EditDelegationPermissionsModalProps {
  delegation: ActiveDelegationModel | null;
  onDelegationChange: (delegation: ActiveDelegationModel | null) => void;
}

const toDateInputValue = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  return isoDate.split("T")[0] ?? "";
};

const getTodayDateInputValue = () => new Date().toISOString().split("T")[0];

const EditDelegationPermissionsModal = ({
  delegation,
  onDelegationChange,
}: EditDelegationPermissionsModalProps) => {
  const updatePermissionsMutation = useUpdateDelegationPermissions();
  const updateValidityMutation = useUpdateDelegationValidity();
  const [permissions, setPermissions] = useState(0);
  const [hasNoExpiry, setHasNoExpiry] = useState(true);
  const [validTo, setValidTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showPermissionError, setShowPermissionError] = useState(false);
  const [showValidityError, setShowValidityError] = useState(false);
  const isOpen = delegation !== null;
  const isPending =
    updatePermissionsMutation.isPending || updateValidityMutation.isPending;

  // TODO(react-hooks-7): this seeds the whole edit form from the `delegation`
  // prop whenever the modal opens. The idiomatic replacement is to remount the
  // modal with key={delegation.id} so the useState initialisers do the seeding,
  // which removes the cascading render this effect causes.
  useEffect(() => {
    if (delegation) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPermissions(delegation.permissions);
      setHasNoExpiry(!delegation.validTo);
      setValidTo(toDateInputValue(delegation.validTo));
      setError(null);
      setShowPermissionError(false);
      setShowValidityError(false);
    }
  }, [delegation]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) onDelegationChange(null);
  };

  const handleSave = async () => {
    if (!delegation) return;

    if (permissions <= 0) {
      setShowPermissionError(true);
      return;
    }

    if (!hasNoExpiry && !validTo) {
      setShowValidityError(true);
      return;
    }

    if (!hasNoExpiry && validTo < getTodayDateInputValue()) {
      setShowValidityError(true);
      setError("Udløbsdato skal være i dag eller i fremtiden.");
      return;
    }

    setError(null);
    setShowPermissionError(false);
    setShowValidityError(false);

    try {
      await updatePermissionsMutation.mutateAsync({
        id: delegation.id,
        model: { permissions },
      });

      await updateValidityMutation.mutateAsync({
        id: delegation.id,
        model: {
          validTo: hasNoExpiry ? null : `${validTo}T00:00:00.000Z`,
        },
      });

      onDelegationChange(null);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Kunne ikke opdatere fuldmagten.",
      );
    }
  };

  const handlePermissionsChange = (nextPermissions: number) => {
    setPermissions(nextPermissions);
    if (nextPermissions > 0) setShowPermissionError(false);
  };

  return (
    <ModalRoot
      controller={{
        open: isOpen,
        setOpen: handleOpenChange,
      }}
    >
      <Modal
        small
        title="Rediger fuldmagt"
        description="Opdater rettigheder og udløb for fuldmagten"
        footer={
          <div className="d-flex justify-content-end gap-2 p-4">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => onDelegationChange(null)}
              disabled={isPending}
            >
              Annuller
            </button>
            <Button
              id="delegation-edit-permissions-save"
              color="primary"
              onClick={handleSave}
              disabled={isPending}
            >
              {isPending ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <Spinner inline size={16} label="" />
                  Gemmer...
                </span>
              ) : (
                "Gem"
              )}
            </Button>
          </div>
        }
      >
        {error ? (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        ) : null}
        <div className="vstack gap-4">
          <DelegationPermissionsField
            permissions={permissions}
            onChange={handlePermissionsChange}
            idPrefix="delegation-edit-perm"
            isRequired
          />
          {showPermissionError ? (
            <div className="text-danger small mt-1">
              Vælg mindst én rettighed.
            </div>
          ) : null}

          <div>
            <p className="mb-2">
              <strong>Nuværende udløb:</strong>{" "}
              {delegation?.validTo
                ? formatDanishLongDate(delegation.validTo)
                : "Ingen udløb"}
            </p>
            <div className="form-check mb-3">
              <input
                id="delegation-edit-no-expiry"
                type="checkbox"
                className="form-check-input"
                checked={hasNoExpiry}
                onChange={(event) => {
                  setHasNoExpiry(event.target.checked);
                  if (event.target.checked) {
                    setValidTo("");
                    setShowValidityError(false);
                  }
                }}
              />
              <label
                className="form-check-label"
                htmlFor="delegation-edit-no-expiry"
              >
                Ingen udløb
              </label>
            </div>
            {!hasNoExpiry ? (
              <div>
                <label
                  className="form-label"
                  htmlFor="delegation-edit-valid-to"
                >
                  Gyldig til
                </label>
                <input
                  id="delegation-edit-valid-to"
                  type="date"
                  className="form-control"
                  value={validTo}
                  min={getTodayDateInputValue()}
                  onChange={(event) => {
                    setValidTo(event.target.value);
                    if (event.target.value) setShowValidityError(false);
                  }}
                />
                {showValidityError ? (
                  <div className="text-danger small mt-1">
                    Angiv en gyldig udløbsdato i dag eller fremtiden.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </Modal>
    </ModalRoot>
  );
};

export default EditDelegationPermissionsModal;
